/**
 * Custom hook that contains collection of functions that redirect to a specific route.
 *
 * Usage:
 *   `const navigateTo = useNavigateTo()`
 *   `navigateTo.projects({ projectId: 'abc-def });`
 */

import { mapValues } from 'lodash-es';
import type { NavigateOptions } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import type { PathParams } from '../helpers/paths';
import { paths } from '../helpers/paths';
import { parseSearchParams } from '../helpers/parse-search-params';

export const useNavigateTo = () => {
  const navigate = useNavigate();

  return {
    ...mapValues(
      paths,
      path =>
        (
          args?: PathParams,
          searchParams?: string | Record<string, string>,
          options?: NavigateOptions,
        ) => {
          navigate(
            { pathname: path(args), search: handleSearchParams(searchParams) },
            options && { ...options },
          );
        },
    ),
  };
};

const handleSearchParams = (
  searchParams?: string | Record<string, string>,
): string | undefined => {
  if (!searchParams) return;

  if (typeof searchParams === 'string') {
    if (searchParams.length === 0) return;
    return searchParams.startsWith('?') ? searchParams : `?${searchParams}`;
  }

  return parseSearchParams(searchParams);
};
